import { useEffect, useRef, useState } from 'react';
import type { Account } from '../../core/accounts';
import { formatMonthKey } from '../../core/dates';
import { planImport, type ImportPlan } from '../../core/import';
import { watchAccounts } from '../../data/accounts';
import type { UserRecord } from '../../data/auth';
import {
  ImportError,
  commitImport,
  findPreviousImport,
  hashFile,
  resolveIssue,
  rollbackImport,
  watchImportIssues,
  watchImports,
  type CommitProgress,
  type ImportIssueRecord,
  type ImportRecord,
} from '../../data/import';
import { WorkbookError, parseWorkbook } from '../import/parseWorkbook';

const SHEET_MONTH: Record<string, string> = {
  January: '2026-01', February: '2026-02', March: '2026-03', April: '2026-04',
  May: '2026-05', June: '2026-06', July: '2026-07', August: '2026-08',
};

type Stage = 'choose' | 'preview' | 'committing' | 'done';

export function ImportScreen({ record }: { record: UserRecord }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [history, setHistory] = useState<ImportRecord[]>([]);
  const [stage, setStage] = useState<Stage>('choose');
  const [file, setFile] = useState<{ file: File; hash: string } | null>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [progress, setProgress] = useState<CommitProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => watchAccounts(setAccounts), []);
  useEffect(() => watchImports(setHistory), []);

  const accountType = (id: string) => accounts.find((a) => a.id === id)?.type;

  async function choose(chosen: File | undefined) {
    if (!chosen) return;
    setError(null);
    setPlan(null);
    try {
      const hash = await hashFile(chosen);

      // FR-IMP-14: refused by content, not by name — renaming must not get a file past.
      const previous = await findPreviousImport(hash);
      if (previous && previous.status === 'committed') {
        setError(
          `This exact file was imported on ${previous.importedAt?.toLocaleDateString() ?? 'an earlier date'}` +
            ` as "${previous.fileName}". Roll that import back first if you want to redo it.`,
        );
        return;
      }

      const parsed = await parseWorkbook(chosen);
      setPlan(planImport(parsed.rows, (sheet) => SHEET_MONTH[sheet] ?? null));
      setFile({ file: chosen, hash });
      setStage('preview');
    } catch (cause) {
      setError(cause instanceof WorkbookError ? cause.message : 'That file could not be read.');
    }
  }

  async function commit() {
    if (!plan || !file) return;
    setStage('committing');
    setError(null);
    try {
      const id = await commitImport(
        plan, file.file.name, file.hash, record.uid, accountType, setProgress,
      );
      setBatchId(id);
      setStage('done');
    } catch (cause) {
      setError(cause instanceof ImportError ? cause.message : 'The import did not finish.');
      setStage('preview');
    }
  }

  return (
    <>
      <h1>Import</h1>

      {error && <p className="error">{error}</p>}

      {stage === 'choose' && (
        <section className="panel">
          <h2>Choose the workbook</h2>
          <p className="muted small">
            Reads the eight monthly sheets. Rows are filed by their own date, not the
            sheet they sit on — two January rows are dated February.
          </p>
          <input
            ref={input}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => void choose(e.target.files?.[0])}
          />
          <button type="button" className="btn btn-primary" onClick={() => input.current?.click()}>
            Choose .xlsx file
          </button>
        </section>
      )}

      {stage === 'preview' && plan && file && (
        <Preview
          plan={plan}
          fileName={file.file.name}
          onCancel={() => { setStage('choose'); setPlan(null); setFile(null); }}
          onCommit={() => void commit()}
        />
      )}

      {stage === 'committing' && (
        <section className="panel">
          <h2>Importing…</h2>
          <p className="muted">
            {progress ? `${progress.written} of ${progress.total} rows written.` : 'Starting…'}
          </p>
        </section>
      )}

      {stage === 'done' && batchId && (
        <ReviewQueue
          batchId={batchId}
          onRollback={async () => {
            const n = await rollbackImport(batchId, accountType);
            setError(`Rolled back ${n} transactions.`);
            setStage('choose');
            setBatchId(null);
            setPlan(null);
            setFile(null);
          }}
        />
      )}

      {history.length > 0 && (
        <section className="panel">
          <h2>Previous imports</h2>
          <table className="table compact">
            <tbody>
              {history.map((record_) => (
                <tr key={record_.id}>
                  <td>
                    {record_.fileName}
                    <div className="muted tiny">
                      {record_.importedAt?.toLocaleString() ?? '—'} ·{' '}
                      {record_.counts.imported} imported, {record_.counts.skipped} skipped
                    </div>
                  </td>
                  <td className="num">
                    {record_.status === 'rolled-back' ? (
                      <span className="badge">rolled back</span>
                    ) : (
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => {
                          setBatchId(record_.id);
                          setStage('done');
                        }}
                      >
                        Review
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function Preview({
  plan, fileName, onCancel, onCommit,
}: {
  plan: ImportPlan;
  fileName: string;
  onCancel: () => void;
  onCommit: () => void;
}) {
  const { summary } = plan;
  const kinds = plan.issues.reduce<Record<string, number>>((acc, issue) => {
    acc[issue.kind] = (acc[issue.kind] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <section className="panel">
      <h2>{fileName}</h2>

      <table className="table compact">
        <tbody>
          <tr><td>Rows read</td><td className="num">{summary.rowsRead}</td></tr>
          <tr><td>Will import</td><td className="num"><strong>{summary.importable}</strong></td></tr>
          <tr>
            <td>Flagged for review</td>
            <td className="num">
              {summary.flagged}
              <div className="muted tiny">imported, then queued</div>
            </td>
          </tr>
          <tr>
            <td>Skipped</td>
            <td className="num">
              {summary.skipped}
              <div className="muted tiny">cannot be imported as they stand</div>
            </td>
          </tr>
        </tbody>
      </table>

      {Object.keys(kinds).length > 0 && (
        <ul className="notes">
          {Object.entries(kinds).map(([kind, n]) => (
            <li key={kind}>{n} × {kind.replace(/-/g, ' ')}</li>
          ))}
        </ul>
      )}

      <h3>By month</h3>
      <p className="muted small">
        Filed by row date. {summary.crossSheetDates > 0 &&
          `${summary.crossSheetDates} rows sit on a sheet for a different month.`}
      </p>
      <ul className="chips">
        {Object.entries(summary.byMonth).sort().map(([month, n]) => (
          <li key={month} className="chip">{formatMonthKey(month)}: {n}</li>
        ))}
      </ul>

      {Object.keys(summary.byRule).length > 0 && (
        <>
          <h3>Reclassified</h3>
          <p className="muted small">
            Card bills and FDs become transfers, not expenses — which is what makes the
            app’s totals differ from the workbook’s.
          </p>
          <ul className="notes">
            {Object.entries(summary.byRule).sort((a, b) => b[1] - a[1]).map(([rule, n]) => (
              <li key={rule}>{rule} — {n} rows</li>
            ))}
          </ul>
        </>
      )}

      <div className="row">
        <button type="button" className="btn btn-primary" onClick={onCommit}>
          Import {summary.importable} rows
        </button>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

/**
 * WD-07 — a worklist that empties, not a wizard step.
 *
 * Flagged rows are already imported. The app is fully usable while this has open items,
 * which is the whole point of FR-IMP-09: import must not become a blocking review
 * session.
 */
function ReviewQueue({ batchId, onRollback }: { batchId: string; onRollback: () => Promise<void> }) {
  const [issues, setIssues] = useState<ImportIssueRecord[]>([]);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => watchImportIssues(batchId, setIssues), [batchId]);

  const open = issues.filter((i) => i.resolution === 'pending');
  const visible = showResolved ? issues : open;

  return (
    <section className="panel">
      <div className="head-row">
        <h2>Import review · {open.length} open</h2>
        <button type="button" className="btn-link danger" onClick={() => void onRollback()}>
          Roll back this import
        </button>
      </div>

      {open.length === 0 && (
        <p className="muted">
          Nothing left to review. Flagged rows were imported either way — this queue only
          ever held the ones worth a second look.
        </p>
      )}

      {visible.map((issue) => (
        <div key={issue.id} className="subpanel">
          <strong>{issue.kind.replace(/-/g, ' ')}</strong>
          <span className="muted tiny"> · {issue.sheet} row {issue.rowNumber}</span>
          <p className="small">{issue.message}</p>
          <p className="muted tiny">
            {issue.raw.account ?? '(no account)'} · {issue.raw.description ?? '(no description)'} ·{' '}
            {issue.raw.amount}
          </p>
          {issue.resolution === 'pending' ? (
            <div className="row">
              {/* Keeping is listed first, deliberately: genuine same-day repeats exist,
                  and a destructive default would quietly lose real data. */}
              <button
                type="button"
                className="btn"
                onClick={() => void resolveIssue(batchId, issue.id, 'corrected')}
              >
                Keep as is
              </button>
              <button
                type="button"
                className="btn-link"
                onClick={() => void resolveIssue(batchId, issue.id, 'skipped')}
              >
                Ignore
              </button>
            </div>
          ) : (
            <span className="badge">{issue.resolution}</span>
          )}
        </div>
      ))}

      {issues.length > open.length && (
        <button type="button" className="btn-link" onClick={() => setShowResolved((v) => !v)}>
          {showResolved ? 'Hide' : 'Show'} {issues.length - open.length} resolved
        </button>
      )}
    </section>
  );
}

